import styled from "@emotion/styled";
import { InputBase, MenuItem, Select, Typography } from "@mui/material";
import { Flex, colors, tagColors } from "@zfp-akademie-a-s/zfp-design";
import { BlockedIcon, CheckCircleIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisStatusChip, analysisStatusColor } from "Components/Analyses/_Shared/AnalysisStatusChip";
import { ChevronDownCircleIcon, InProgressIcon } from "Components/_Shared/Icons/Icons";
import { AnalysisDetailState } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: isPropValid,
})<{ $selected: boolean }>`
  display: ${(props) => (props.$selected ? "none" : "block")};
  border-radius: ${(props) => props.theme.spacing(Radius.S)};

  &.Mui-selected {
    background: transparent;
  }

  &.Mui-selected:hover, &.Mui-focusVisible {
    background: ${colors.backgroundSecondary} !important;
  }
`;

type Props = {
  readonly state: AnalysisDetailState;
  readonly onStateChange: (state: AnalysisDetailState) => void;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
};

type VariantMap = {
  [key in AnalysisDetailState]: {
    readonly text: string;
    readonly icon: React.ReactNode;
  };
};

// The header's status: the filled status chip, opening a menu of the other states in the chip's colours.
export const AnalysisStatusSelect: React.FC<Props> = ({ state, onStateChange, disabled, readOnly = false }) => {
  const { t } = useResource();
  const variantMap: VariantMap = {
    [AnalysisDetailState.in_progress]: {
      text: t(Resources.AnalysesListing.Status.InProgress),
      icon: <InProgressIcon sx={{ fontSize: 16 }} />,
    },
    [AnalysisDetailState.completed]: {
      text: t(Resources.AnalysesListing.Status.Completed),
      icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
    },
    [AnalysisDetailState.declined]: {
      text: t(Resources.AnalysesListing.Status.Declined),
      icon: <BlockedIcon sx={{ fontSize: 16 }} />,
    },
  };

  return (
    <Select
      disabled={disabled}
      readOnly={readOnly}
      onChange={(event) => onStateChange(event.target.value as AnalysisDetailState)}
      value={state}
      input={<InputBase />}
      IconComponent={() => null}
      renderValue={(value) => (
        <AnalysisStatusChip
          state={value}
          variant="filled"
          disabled={disabled}
          endIcon={readOnly ? undefined : <ChevronDownCircleIcon sx={{ fontSize: 20 }} />}
        />
      )}
      sx={{
        // && outranks the theme's 40px select and input heights
        "&& .MuiSelect-select": {
          display: "flex",
          padding: "0 !important",
          height: "auto",
          lineHeight: "normal",
          minHeight: 0,
          borderRadius: 999,
          cursor: readOnly ? "default" : "pointer",
          "&:focus": { backgroundColor: "transparent" },
        },
        "&.Mui-focused .MuiSelect-select:focus-visible": { outline: 2, outlineColor: "primary.main", outlineOffset: 2 },
      }}
      MenuProps={{
        slotProps: {
          paper: {
            sx: {
              paddingX: Spacing.S,
            },
          },
        },
      }}
    >
      {Object.values(AnalysisDetailState).map((value) => {
        return (
          <StyledMenuItem key={value} value={value} $selected={value === state}>
            <Flex alignItems="center">
              <Flex alignItems="center" color={tagColors[analysisStatusColor[value]].icon} mr={Spacing.M}>
                {variantMap[value].icon}
              </Flex>
              <Typography lineHeight={1.4} variant="body2" sx={{ color: tagColors[analysisStatusColor[value]].text }}>
                {variantMap[value].text}
              </Typography>
            </Flex>
          </StyledMenuItem>
        );
      })}
    </Select>
  );
};
